import { useState } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { Button } from "../ui/button"
import { Input } from "../ui/input"
import { Label } from "../ui/label"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card"
import { Alert, AlertDescription } from "../ui/alert"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select"
import { SearchableSelect } from "../ui/searchable-select"
import { Eye, EyeOff, Lock, Mail, User, Brain, CheckCircle, Phone, ArrowLeft } from "lucide-react"
import { Link, useNavigate } from "react-router-dom"
import { apiService } from "../../lib/api"
import { formatErrorMessage } from "../../lib/utils"

// Brand colors from landing page and my-account
const BRAND_COLORS = {
  primary: '#c39d76', // Warm beige/tan
  secondary: '#d4a574', // Lighter tan
  dark: '#2C2416', // Dark brown
  darkGrey: '#3F454A', // Dark grey
  accent: '#dfc9b1', // Light beige
}

const registerSchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters"),
  email: z.string().email("Please enter a valid email address"),
  phone: z.string().min(1, "Phone number is required").regex(/^[\d\s\-\+\(\)]+$/, "Please enter a valid phone number"),
  country: z.string().min(1, "Country is required"),
  idType: z.string().min(1, "ID type is required"),
  passportNumber: z.string().optional(),
  idNumber: z.string().optional(),
  password: z.string().min(8, "Password must be at least 8 characters"),
  confirmPassword: z.string().min(8, "Please confirm your password"),
})
.refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
})
.refine((data) => {
  // ID type is required, so the corresponding ID number must be provided
  if (data.idType === 'passport') {
    if (!data.passportNumber || data.passportNumber.trim().length < 6) {
      return false;
    }
    return /^[A-Z0-9\-]{6,20}$/i.test(data.passportNumber.trim());
  }
  if (data.idType === 'national_id') {
    if (!data.idNumber || data.idNumber.trim().length < 6) {
      return false;
    }
    return /^[A-Z0-9\-]{6,20}$/i.test(data.idNumber.trim());
  }
  return false; // ID type is required
}, {
  message: "ID number is required and must be 6-20 alphanumeric characters (may include hyphens)",
  path: ["idNumber"],
})

type RegisterFormData = z.infer<typeof registerSchema>

// Comprehensive countries list for registration form
const COUNTRIES = [
  // African Countries (prioritized)
  { value: 'RW', label: 'Rwanda' },
  { value: 'KE', label: 'Kenya' },
  { value: 'UG', label: 'Uganda' },
  { value: 'TZ', label: 'Tanzania' },
  { value: 'ET', label: 'Ethiopia' },
  { value: 'ZA', label: 'South Africa' },
  { value: 'NG', label: 'Nigeria' },
  { value: 'GH', label: 'Ghana' },
  { value: 'EG', label: 'Egypt' },
  { value: 'MA', label: 'Morocco' },
  { value: 'DZ', label: 'Algeria' },
  { value: 'TN', label: 'Tunisia' },
  { value: 'SN', label: 'Senegal' },
  { value: 'CI', label: 'Côte d\'Ivoire' },
  { value: 'CM', label: 'Cameroon' },
  { value: 'AO', label: 'Angola' },
  { value: 'SD', label: 'Sudan' },
  { value: 'MZ', label: 'Mozambique' },
  { value: 'MG', label: 'Madagascar' },
  { value: 'ZM', label: 'Zambia' },
  { value: 'ZW', label: 'Zimbabwe' },
  { value: 'ML', label: 'Mali' },
  { value: 'BF', label: 'Burkina Faso' },
  { value: 'NE', label: 'Niger' },
  { value: 'TD', label: 'Chad' },
  { value: 'SO', label: 'Somalia' },
  { value: 'ER', label: 'Eritrea' },
  { value: 'DJ', label: 'Djibouti' },
  { value: 'BI', label: 'Burundi' },
  { value: 'CD', label: 'Democratic Republic of the Congo' },
  { value: 'CG', label: 'Republic of the Congo' },
  { value: 'CF', label: 'Central African Republic' },
  { value: 'SS', label: 'South Sudan' },
  { value: 'LY', label: 'Libya' },
  { value: 'MR', label: 'Mauritania' },
  { value: 'GM', label: 'Gambia' },
  { value: 'GW', label: 'Guinea-Bissau' },
  { value: 'GN', label: 'Guinea' },
  { value: 'SL', label: 'Sierra Leone' },
  { value: 'LR', label: 'Liberia' },
  { value: 'TG', label: 'Togo' },
  { value: 'BJ', label: 'Benin' },
  { value: 'GA', label: 'Gabon' },
  { value: 'GQ', label: 'Equatorial Guinea' },
  { value: 'ST', label: 'São Tomé and Príncipe' },
  { value: 'BW', label: 'Botswana' },
  { value: 'NA', label: 'Namibia' },
  { value: 'LS', label: 'Lesotho' },
  { value: 'SZ', label: 'Eswatini' },
  { value: 'MW', label: 'Malawi' },
  { value: 'MU', label: 'Mauritius' },
  { value: 'SC', label: 'Seychelles' },
  { value: 'KM', label: 'Comoros' },
  { value: 'CV', label: 'Cape Verde' },
  // Major Countries
  { value: 'US', label: 'United States' },
  { value: 'CA', label: 'Canada' },
  { value: 'GB', label: 'United Kingdom' },
  { value: 'FR', label: 'France' },
  { value: 'DE', label: 'Germany' },
  { value: 'IT', label: 'Italy' },
  { value: 'ES', label: 'Spain' },
  { value: 'NL', label: 'Netherlands' },
  { value: 'BE', label: 'Belgium' },
  { value: 'CH', label: 'Switzerland' },
  { value: 'AT', label: 'Austria' },
  { value: 'SE', label: 'Sweden' },
  { value: 'NO', label: 'Norway' },
  { value: 'DK', label: 'Denmark' },
  { value: 'FI', label: 'Finland' },
  { value: 'PL', label: 'Poland' },
  { value: 'PT', label: 'Portugal' },
  { value: 'GR', label: 'Greece' },
  { value: 'IE', label: 'Ireland' },
  { value: 'AU', label: 'Australia' },
  { value: 'NZ', label: 'New Zealand' },
  { value: 'JP', label: 'Japan' },
  { value: 'CN', label: 'China' },
  { value: 'IN', label: 'India' },
  { value: 'KR', label: 'South Korea' },
  { value: 'SG', label: 'Singapore' },
  { value: 'MY', label: 'Malaysia' },
  { value: 'TH', label: 'Thailand' },
  { value: 'VN', label: 'Vietnam' },
  { value: 'PH', label: 'Philippines' },
  { value: 'ID', label: 'Indonesia' },
  { value: 'BR', label: 'Brazil' },
  { value: 'MX', label: 'Mexico' },
  { value: 'AR', label: 'Argentina' },
  { value: 'CL', label: 'Chile' },
  { value: 'CO', label: 'Colombia' },
  { value: 'PE', label: 'Peru' },
  { value: 'AE', label: 'United Arab Emirates' },
  { value: 'SA', label: 'Saudi Arabia' },
  { value: 'IL', label: 'Israel' },
  { value: 'TR', label: 'Turkey' },
  { value: 'RU', label: 'Russia' },
  { value: 'UA', label: 'Ukraine' },
  { value: 'PK', label: 'Pakistan' },
  { value: 'BD', label: 'Bangladesh' },
  { value: 'LK', label: 'Sri Lanka' },
  { value: 'NP', label: 'Nepal' },
  { value: 'MM', label: 'Myanmar' },
  { value: 'KH', label: 'Cambodia' },
  { value: 'LA', label: 'Laos' },
  { value: 'MN', label: 'Mongolia' },
  { value: 'KZ', label: 'Kazakhstan' },
  { value: 'UZ', label: 'Uzbekistan' },
  { value: 'IQ', label: 'Iraq' },
  { value: 'IR', label: 'Iran' },
  { value: 'AF', label: 'Afghanistan' },
  { value: 'JO', label: 'Jordan' },
  { value: 'LB', label: 'Lebanon' },
  { value: 'SY', label: 'Syria' },
  { value: 'YE', label: 'Yemen' },
  { value: 'OM', label: 'Oman' },
  { value: 'KW', label: 'Kuwait' },
  { value: 'QA', label: 'Qatar' },
  { value: 'BH', label: 'Bahrain' },
  { value: 'CY', label: 'Cyprus' },
  { value: 'MT', label: 'Malta' },
  { value: 'IS', label: 'Iceland' },
  { value: 'LU', label: 'Luxembourg' },
  { value: 'CZ', label: 'Czech Republic' },
  { value: 'HU', label: 'Hungary' },
  { value: 'RO', label: 'Romania' },
  { value: 'BG', label: 'Bulgaria' },
  { value: 'HR', label: 'Croatia' },
  { value: 'SI', label: 'Slovenia' },
  { value: 'SK', label: 'Slovakia' },
  { value: 'EE', label: 'Estonia' },
  { value: 'LV', label: 'Latvia' },
  { value: 'LT', label: 'Lithuania' },
  { value: 'Other', label: 'Other' },
].sort((a, b) => a.label.localeCompare(b.label));

export function RegisterForm() {
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState(false)
  const [registeredEmail, setRegisteredEmail] = useState("")
  const [idType, setIdType] = useState("")
  const [passportNumber, setPassportNumber] = useState("")
  const [idNumber, setIdNumber] = useState("")
  const [country, setCountry] = useState("")
  const navigate = useNavigate()

  const {
    register: registerForm,
    handleSubmit: handleRegisterSubmit,
    formState: { errors: registerErrors },
    setValue,
    trigger,
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
  })

  const onRegisterSubmit = async (data: RegisterFormData) => {
    setIsLoading(true)
    setError("")
    setSuccess(false)

    try {
      // Convert country code to country name for storage
      const countryName = country 
        ? COUNTRIES.find(c => c.value === country)?.label || country
        : '';
      
      // Prepare registration data
      const registerData: any = {
        email: data.email,
        password: data.password,
        name: data.name,
        phone: data.phone.trim(),
        country: countryName, // Required field
      };
      
      // Add ID fields based on selected type (required)
      if (idType === 'passport' && passportNumber) {
        registerData.passportNumber = passportNumber.trim();
      } else if (idType === 'national_id' && idNumber) {
        registerData.idNumber = idNumber.trim();
      }
      
      const response = await apiService.register(registerData)

      console.log("Registration successful:", response)
      setRegisteredEmail(data.email)
      setSuccess(true)

      // Redirect to email verification page after a short delay
      setTimeout(() => {
        navigate(`/verify-email?email=${encodeURIComponent(data.email)}`)
      }, 2000)

    } catch (err: any) {
      console.error("Registration failed:", err)
      
      // Handle specific error codes
      let errorMessage = formatErrorMessage(err)
      
      // Check for phone_exists error
      if (err?.response?.data?.error?.code === 'phone_exists' || 
          err?.message?.includes('phone_exists') ||
          err?.message?.toLowerCase().includes('phone number already exists')) {
        errorMessage = 'This phone number is already registered. Please use a different phone number.'
      }
      // Check for email_exists error
      else if (err?.response?.data?.error?.code === 'email_exists' || 
               err?.message?.includes('email_exists') ||
               err?.message?.toLowerCase().includes('email already exists')) {
        errorMessage = 'This email is already registered. Please use a different email or try logging in.'
      }
      
      setError(errorMessage)
      setIsLoading(false)
    }
  }

  if (success) {
    return (
      <div className="min-h-screen flex">
        {/* Left Side - Image with Black Overlay */}
        <div className="hidden lg:flex lg:w-1/2 relative">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{
               backgroundImage: "url(/log-sin-bg.jpg)",
            }}
          >
            <div className="absolute inset-0 bg-black/60"></div>
          </div>
          <div className="relative z-10 flex items-center justify-center w-full p-12">
            <div className="text-center text-white space-y-6">
              <img 
                src="/brand-b.png" 
                alt="Brand Logo" 
                className="h-24 w-24 object-contain mx-auto mb-8"
              />
              <h1 className="text-4xl font-bold">Welcome to IKORO</h1>
              <p className="text-lg text-gray-200">Join us and start your journey with ikoro</p>
            </div>
          </div>
        </div>

        {/* Right Side - Success Message */}
        <div className="w-full lg:w-1/2 flex items-center justify-center p-4 bg-white relative">
          {/* Back to Home Link */}
          <Link
            to="/"
            className="absolute top-4 left-4 text-sm hover:underline font-medium flex items-center gap-1 z-10"
            style={{ color: BRAND_COLORS.primary }}
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Back to Home</span>
          </Link>
          
          <div className="w-full max-w-lg">
          <div className="text-center space-y-2 pb-4 pt-8 sm:pt-4">
            <div className="mx-auto w-12 h-12 rounded-xl flex items-center justify-center" style={{
              background: `linear-gradient(135deg, ${BRAND_COLORS.secondary} 0%, ${BRAND_COLORS.primary} 100%)`,
            }}>
              <Mail className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-lg font-bold" style={{ color: BRAND_COLORS.dark }}>
              Registration Successful!
            </h1>
            <p className="text-sm" style={{ color: BRAND_COLORS.darkGrey }}>
              Your account has been created. Please check your email to verify your account. You will be redirected to the verification page shortly.
            </p>
          </div>
          <div className="text-center px-3 sm:px-6 pb-6">
            <Button
              onClick={() => navigate(`/verify-email?email=${encodeURIComponent(registeredEmail)}`)}
              className="w-full font-semibold py-2.5"
              style={{
                background: `linear-gradient(135deg, ${BRAND_COLORS.secondary} 0%, ${BRAND_COLORS.primary} 100%)`,
                color: 'white',
              }}
            >
              Go to Email Verification
            </Button>
          </div>
        </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Image with Black Overlay */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url(/log-sin-bg.jpg)",
          }}
        >
          <div className="absolute inset-0 bg-black/60"></div>
        </div>
        <div className="relative z-10 flex items-center justify-center w-full p-12">
          <div className="text-center text-white space-y-6">
            <img 
              src="/brand-b.png" 
              alt="Brand Logo" 
              className="h-24 w-24 object-contain mx-auto mb-8"
            />
            <h1 className="text-4xl font-bold">Welcome to IKORO</h1>
            <p className="text-lg text-gray-200">Join us and start your journey with ikoro</p>
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-4 bg-white relative">
        {/* Back to Home Link */}
        <Link
          to="/"
          className="absolute top-4 left-4 text-sm hover:underline font-medium flex items-center gap-1 z-10"
          style={{ color: BRAND_COLORS.primary }}
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="hidden sm:inline">Back to Home</span>
        </Link>
        
        <div className="w-full max-w-md">
        <div className="text-center space-y-2 pb-4 pt-8 sm:pt-4">
          {/* Brand Logo and Title in One Row */}
          <div className="flex items-center justify-center gap-3 mb-2">
            <img 
              src="/brand-b.png" 
              alt="Brand Logo" 
              className="h-12 w-12 object-contain"
            />
            <h1 className="text-xl font-bold m-0" style={{ color: BRAND_COLORS.dark }}>
              Join IKORO
            </h1>
          </div>
         
        </div>

        <div className="space-y-3 px-3 sm:px-6 pb-6">
          {error && (
            <Alert variant="destructive" className="border-red-500 bg-red-50">
              <AlertDescription className="text-red-600">{error}</AlertDescription>
            </Alert>
          )}

          <form
            onSubmit={(e) => {
              e.preventDefault()
              handleRegisterSubmit(onRegisterSubmit)(e)
            }}
            className="space-y-3"
          >
            <div className="space-y-1.5">
              <Label htmlFor="name" className="text-sm">Full Name</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4" style={{ color: BRAND_COLORS.primary }} />
                <Input
                  id="name"
                  type="text"
                  placeholder="Enter your full name"
                  className={`pl-10 ${registerErrors.name ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
                  style={registerErrors.name ? {} : { borderColor: `${BRAND_COLORS.primary}40` }}
                  {...registerForm("name")}
                  disabled={isLoading}
                />
              </div>
              {registerErrors.name && (
                <p className="text-sm text-red-600 font-medium">{registerErrors.name.message}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-sm">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4" style={{ color: BRAND_COLORS.primary }} />
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  className={`pl-10 ${registerErrors.email ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
                  style={registerErrors.email ? {} : { borderColor: `${BRAND_COLORS.primary}40` }}
                  {...registerForm("email")}
                  disabled={isLoading}
                />
              </div>
              {registerErrors.email && (
                <p className="text-sm text-red-600 font-medium">{registerErrors.email.message}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="phone" className="text-sm">Phone Number <span className="text-red-500">*</span></Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4" style={{ color: BRAND_COLORS.primary }} />
                <Input
                  id="phone"
                  type="tel"
                  placeholder="Enter your phone number"
                  className={`pl-10 ${registerErrors.phone ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
                  style={registerErrors.phone ? {} : { borderColor: `${BRAND_COLORS.primary}40` }}
                  {...registerForm("phone")}
                  disabled={isLoading}
                  required
                />
              </div>
              {registerErrors.phone && (
                <p className="text-sm text-red-600 font-medium">{registerErrors.phone.message}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="country" className="text-sm">Country <span className="text-red-500">*</span></Label>
              <SearchableSelect
                options={COUNTRIES}
                value={country}
                onValueChange={(value) => {
                  setCountry(value);
                  setValue('country', value);
                  trigger('country');
                }}
                placeholder="Select country..."
                searchPlaceholder="Search countries..."
                emptyText="No countries found."
                className={`w-full ${registerErrors.country ? 'border-red-500' : ''}`}
              />
              {registerErrors.country && (
                <p className="text-sm text-red-600 font-medium">{registerErrors.country.message}</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="idType" className="text-sm">ID Type <span className="text-red-500">*</span></Label>
                <Select
                  value={idType}
                  onValueChange={(value) => {
                    setIdType(value);
                    setValue('idType', value);
                    // Clear the other field when switching types
                    if (value === 'passport') {
                      setIdNumber('');
                      setValue('idNumber', '');
                    } else if (value === 'national_id') {
                      setPassportNumber('');
                      setValue('passportNumber', '');
                    } else {
                      setPassportNumber('');
                      setIdNumber('');
                      setValue('passportNumber', '');
                      setValue('idNumber', '');
                    }
                    // Trigger validation for ID fields
                    trigger(['idType', 'passportNumber', 'idNumber']);
                  }}
                  disabled={isLoading}
                >
                  <SelectTrigger className={`w-full ${registerErrors.idType ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`} style={registerErrors.idType ? {} : { borderColor: `${BRAND_COLORS.primary}40` }}>
                    <SelectValue placeholder="Select ID type..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="passport">Passport</SelectItem>
                    <SelectItem value="national_id">National ID</SelectItem>
                  </SelectContent>
                </Select>
                {registerErrors.idType && (
                  <p className="text-sm text-red-600 font-medium">{registerErrors.idType.message}</p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="idNumber" className="text-sm">
                  {idType === 'passport' ? 'Passport Number' : idType === 'national_id' ? 'National ID Number' : 'ID Number'}
                  {idType && <span className="text-red-500 ml-1">*</span>}
                </Label>
                <Input
                  id="idNumber"
                  type="text"
                  placeholder={idType === 'passport' ? 'Enter passport number' : idType === 'national_id' ? 'Enter national ID number' : 'Select ID type first'}
                  value={idType === 'passport' ? passportNumber : idNumber}
                  onChange={(e) => {
                    // Only allow alphanumeric and hyphens
                    const value = e.target.value.replace(/[^A-Z0-9\-]/gi, '');
                    if (idType === 'passport') {
                      setPassportNumber(value);
                      setValue('passportNumber', value);
                      trigger('passportNumber');
                    } else if (idType === 'national_id') {
                      setIdNumber(value);
                      setValue('idNumber', value);
                      trigger('idNumber');
                    }
                  }}
                  maxLength={20}
                  className={`w-full ${(idType === 'passport' && registerErrors.passportNumber) || (idType === 'national_id' && registerErrors.idNumber) ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
                  style={(idType === 'passport' && registerErrors.passportNumber) || (idType === 'national_id' && registerErrors.idNumber) ? {} : { borderColor: `${BRAND_COLORS.primary}40` }}
                  disabled={!idType || isLoading}
                />
                {idType === 'passport' && registerErrors.passportNumber && (
                  <p className="text-sm text-red-600 font-medium">{registerErrors.passportNumber.message}</p>
                )}
                {idType === 'national_id' && registerErrors.idNumber && (
                  <p className="text-sm text-red-600 font-medium">{registerErrors.idNumber.message}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="password" className="text-sm">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4" style={{ color: BRAND_COLORS.primary }} />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Create a password"
                    className={`pl-10 pr-10 ${registerErrors.password ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
                    style={registerErrors.password ? {} : { borderColor: `${BRAND_COLORS.primary}40` }}
                    {...registerForm("password")}
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 hover:opacity-70"
                    style={{ color: BRAND_COLORS.primary }}
                    disabled={isLoading}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {registerErrors.password && (
                  <p className="text-sm text-red-600 font-medium">{registerErrors.password.message}</p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="confirmPassword" className="text-sm">Confirm Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4" style={{ color: BRAND_COLORS.primary }} />
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="Confirm password"
                    className={`pl-10 pr-10 ${registerErrors.confirmPassword ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
                    style={registerErrors.confirmPassword ? {} : { borderColor: `${BRAND_COLORS.primary}40` }}
                    {...registerForm("confirmPassword")}
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 hover:opacity-70"
                    style={{ color: BRAND_COLORS.primary }}
                    disabled={isLoading}
                  >
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {registerErrors.confirmPassword && (
                  <p className="text-sm text-red-600 font-medium">{registerErrors.confirmPassword.message}</p>
                )}
              </div>
            </div>

            <Button
              type="submit"
              className="w-full font-semibold py-2.5"
              disabled={isLoading}
              style={{
                background: `linear-gradient(135deg, ${BRAND_COLORS.secondary} 0%, ${BRAND_COLORS.primary} 100%)`,
                color: 'white',
              }}
            >
              {isLoading ? "Creating account..." : "Create Account"}
            </Button>
          </form>

          <div className="text-center pt-1">
            <span className="text-xs" style={{ color: BRAND_COLORS.darkGrey }}>Already have an account? </span>
            <Link
              to="/login"
              className="text-xs hover:underline font-medium"
              style={{ color: BRAND_COLORS.primary }}
            >
              Sign in
            </Link>
          </div>
        </div>
      </div>
      </div>
    </div>
  )
}
